import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, RefreshCw } from "lucide-react";
import { Badge, Button, MetricCard, PageHeader, PageState, SegmentedControl, Skeleton, StatusDot, cn, type MetricDelta } from "@fox1ck-org/ui";

import { cardDelta } from "@/components/campaign/kpis";
import { MetricGrid, type MetricTile } from "@/components/common/metric-grid";
import { HowItWorks, ReadinessChecklist } from "@/components/today/readiness";
import { RunChart } from "@/components/today/run-chart";
import { useAttention, useToday, useTodayRows, type TodayRow } from "@/components/today/use-today";
import type { AttentionRow } from "@/components/health/issues";
import { useIsPhone } from "@/lib/hooks/use-phone";
import { getLocale, useT } from "@/lib/i18n";
import { DASH, formatAge, formatInt, formatMoney, formatPct } from "@/lib/format";
import type { Bag } from "@/lib/campaigns-table";
import type { TodayModel } from "@/lib/today";

type Series = "profit" | "leads" | "clicks";

/**
 * «Сегодня» (доски Today, TodayFirstRun) — the first screen a buyer opens:
 * the day's seven numbers against yesterday by the same minute, the running
 * total by hour against yesterday's, what needs attention, and the seven best
 * and worst campaigns of the day — all ranked and flagged by the server. The
 * day is the workspace's (decision 03), «Мои · Все» is the Top bar's.
 * Read-only: every row leads to a place (decision 10). Loading, an error and
 * the first run are each a state that ends; nothing waits forever.
 *
 * On a phone (доска MToday) the same day, drawn narrower: the profit to
 * this minute, its run by hour, six of the numbers and what needs attention.
 */
export default function TodayPage() {
  const t = useT();
  const today = useToday();
  const phone = useIsPhone();
  const { state, tz, clock, model, day, updatedAt } = today;
  const at = hhmm(clock);
  const title = t("today.title.date", { date: humanDate(today.today, getLocale()) });
  const [now] = useState(() => Date.now());
  const updated = updatedAt ? Math.max(0, (now - updatedAt) / 1000) : undefined;

  if (phone) return <TodayPhone today={today} at={at} />;

  if (state === "first") {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
        <PageHeader title={title} subtitle={t("ops.first.subtitle")} actions={<span className="text-xs text-muted-foreground">{t("ops.today.day", { tz })}</span>} />
        <Strip v={undefined} p={undefined} at={at} empty />
        <div className="flex min-h-0 flex-1 items-start gap-3">
          <ReadinessChecklist />
          <HowItWorks />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
      <PageHeader
        title={title}
        actions={
          <>
            {state === "ready" && updated !== undefined ? (
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                <StatusDot tone={today.error ? "warn" : "ok"} />
                {today.error ? t("ops.today.stale") : t("today.updated", { age: formatAge(updated) })}
              </span>
            ) : null}
            <span className="whitespace-nowrap text-xs text-muted-foreground">{t("ops.today.day", { tz })}</span>
            <Button variant="secondary" onClick={today.retry}>
              <RefreshCw className="size-4" strokeWidth={1.5} />
              {t("ops.refresh")}
            </Button>
          </>
        }
      />
      {state === "error" ? (
        <PageState mode="error" title={t("today.error")} error={today.error} retry={today.retry} retryLabel={t("lib.common.retry")} />
      ) : (
        <>
          <Strip v={model?.today} p={model?.yesterdayByNow} at={at} loading={state === "loading"} />
          <div className="flex min-h-0 flex-1 gap-3">
            <ChartPanel model={model} at={at} loading={state === "loading"} />
            <Attention day={day.data} loading={state === "loading"} />
          </div>
          <div className="flex min-h-0 flex-1 gap-3">
            <Leaders title={t("today.best")} note={<Link to="/campaigns" className="hover:text-foreground">{t("ops.today.allCampaigns")}</Link>} items={day.data?.best} loading={state === "loading"} empty={t("ops.today.best.empty")} />
            <Leaders title={t("today.worst")} note={t("ops.today.byProfit")} items={day.data?.worst} loading={state === "loading"} empty={t("ops.today.worst.empty")} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Клики · Лиды · Апрувы · Доход · Расход · Профит · ROI (доска Today), each
 * against yesterday by the same minute; on the first run (доска
 * TodayFirstRun) six dashes with what each is waiting for.
 */
function Strip({ v, p, at, loading, empty }: { v: Bag | undefined; p: Bag | undefined; at: string; loading?: boolean; empty?: boolean }) {
  const t = useT();
  const signed = (n: number | undefined) => (n === undefined || n === 0 ? "neutral" : n > 0 ? "ok" : "danger");
  const tiles: { key: string; label: string; value: string; delta?: MetricDelta; note?: string; tone?: "ok" | "danger" | "neutral" }[] = empty
    ? (["clicks", "leads", "approved", "revenue", "cost", "profit"] as const).map((k) => ({ key: k, label: t(`cols.${k}`), value: DASH, note: t("ops.first.waiting") }))
    : [
        { key: "clicks", label: t("cols.clicks"), value: formatInt(v?.clicks), delta: cardDelta("clicks", "count", v?.clicks, p?.clicks), note: t("ops.today.wasAt", { at, value: formatInt(p?.clicks) }) },
        { key: "leads", label: t("cols.leads"), value: formatInt(v?.leads), delta: cardDelta("leads", "count", v?.leads, p?.leads), note: t("kpi.cr", { cr: formatPct(v?.cr) }) },
        { key: "approved", label: t("cols.approved"), value: formatInt(v?.conversions), note: t("today.byTodaysLeads") },
        { key: "revenue", label: t("cols.revenue"), value: formatMoney(v?.revenue), delta: cardDelta("revenue", "count", v?.revenue, p?.revenue), note: t("kpi.epc", { epc: formatMoney(v?.epc, { unit: true }) }) },
        { key: "cost", label: t("cols.cost"), value: formatMoney(v?.cost), delta: cardDelta("cost", "count", v?.cost, p?.cost), note: t("kpi.cpa", { cpa: formatMoney(v?.cpa, { unit: true }) }) },
        {
          key: "profit",
          label: t("cols.profit"),
          value: formatMoney(v?.profit, { signed: true }),
          delta: cardDelta("profit", "profit", v?.profit, p?.profit),
          note: p ? t("ops.today.yesterday", { value: formatMoney(p.profit, { signed: true }) }) : undefined,
          tone: signed(v?.profit),
        },
        { key: "roi", label: t("cols.roi"), value: formatPct(v?.roi, { digits: 0, signed: true }), delta: cardDelta("roi", "pp", v?.roi, p?.roi), tone: signed(v?.roi) },
      ];
  return (
    <div className={cn("grid shrink-0 overflow-hidden rounded-lg border border-border bg-card", empty ? "grid-cols-6" : "grid-cols-7")} role="group" aria-label={t("nav.today")}>
      {tiles.map((k) => (
        <MetricCard
          labelCase="none"
          key={k.key}
          label={k.label}
          value={k.value}
          delta={k.delta}
          comparison={k.note}
          tone={k.tone}
          loading={loading}
          className="min-w-0 rounded-none border-0 border-l border-border px-3.5 py-2.5 first:border-l-0"
        />
      ))}
    </div>
  );
}

const SERIES: { key: Series; label: "cols.profit" | "cols.leads" | "cols.clicks" }[] = [
  { key: "profit", label: "cols.profit" },
  { key: "leads", label: "cols.leads" },
  { key: "clicks", label: "cols.clicks" },
];

function ChartPanel({ model, at, loading }: { model: TodayModel | undefined; at: string; loading: boolean }) {
  const t = useT();
  const [series, setSeries] = useState<Series>("profit");
  const money = series === "profit";
  const fmt = (n: number | undefined) => (money ? formatMoney(n, { signed: true }) : formatInt(n));
  const v = model?.today;
  const now = v?.[series];
  const was = model?.yesterdayByNow[series];
  const yesterdayEnd = model?.runYesterday[23]?.[series];
  return (
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card" aria-label={t(`today.chart.${series}`)}>
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border px-3.5">
        <h2 className="text-section-title">{t(`today.chart.${series}`)}</h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <i aria-hidden className="inline-block h-0.5 w-3 rounded-full bg-primary" />
          {t("today.legend.today")}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <i aria-hidden className="inline-block h-0.5 w-3 rounded-full bg-muted-foreground" />
          {t("today.legend.yesterday")}
        </span>
        <SegmentedControl
          size="sm"
          className="ml-auto"
          aria-label={t("today.chart.series")}
          value={series}
          onValueChange={setSeries}
          options={SERIES.map((s) => ({ value: s.key, label: t(s.label) }))}
        />
      </header>
      {loading || !model || !v ? (
        <Skeleton className="m-3.5 flex-1 rounded-md" />
      ) : (
        <div className="flex min-h-0 flex-1 gap-6 px-3.5 py-3">
          <div className="flex w-37.5 shrink-0 flex-col gap-1">
            <span className="text-xs text-muted-foreground">{t("today.now", { at })}</span>
            <span className={cn("font-mono text-3xl font-semibold tabular-nums", money && (now ?? 0) > 0 && "text-ok", money && (now ?? 0) < 0 && "text-destructive")}>{fmt(now)}</span>
            {now !== undefined && was !== undefined ? (
              <span className="text-xs text-muted-foreground">
                <span className={cn(now - was > 0 ? "text-ok" : now - was < 0 ? "text-destructive" : "")}>{money ? formatMoney(now - was, { signed: true }) : (now - was >= 0 ? "+" : "") + formatInt(now - was)}</span>{" "}
                {t("today.vsYesterdaySameTime")}
              </span>
            ) : null}
            <span className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">{t("today.inHold")}</span>
            <span className="font-mono text-lg font-semibold tabular-nums">≈ {formatMoney(v.revenue_in_transit)}</span>
            <span className="text-xs text-muted-foreground">
              {t("ops.today.holdLeads", { leads: formatInt(v.conversions_hold), rate: formatPct(v.approve_rate, { digits: 0 }) })}
            </span>
          </div>
          <RunChart
            today={model.runToday.map((b) => b[series] ?? 0)}
            yesterday={model.runYesterday.map((b) => b[series] ?? 0)}
            fmt={fmt}
            nowLabel={t("today.chart.nowLabel", { value: fmt(now) })}
            wasLabel={t("ops.today.chart.was", { value: fmt(was) })}
            endLabel={yesterdayEnd !== undefined ? t("ops.today.chart.end", { value: fmt(yesterdayEnd) }) : undefined}
          />
        </div>
      )}
    </section>
  );
}

/** «Требует внимания» — the server's flags of the day, then what data health says. */
function Attention({ day, loading }: { day: Parameters<typeof useAttention>[0]; loading: boolean }) {
  const t = useT();
  const items = useAttention(day);
  return (
    <section className="flex w-103.5 shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card" aria-label={t("today.attention")}>
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3.5">
        <h2 className="text-section-title">{t("today.attention")}</h2>
        {items.length > 0 ? (
          <Badge variant="warn" className="ml-auto">
            {formatInt(items.length)}
          </Badge>
        ) : null}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col gap-2 p-3.5">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="px-3.5 py-6 text-center text-sm text-muted-foreground">{t("today.attention.calm")}</p>
        ) : (
          items.map((r, i) => <AttentionLine key={i} r={r} />)
        )}
      </div>
    </section>
  );
}

function AttentionLine({ r }: { r: AttentionRow }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2.5 last:border-b-0">
      <StatusDot tone={r.tone === "info" ? "info" : r.tone} className="shrink-0 self-start mt-1.5" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{r.title}</span>
        <span className="truncate text-xs text-muted-foreground">{r.sub}</span>
      </div>
      {r.action ? (
        <Button asChild variant="secondary" size="sm">
          <Link to={r.action.to}>{r.action.label}</Link>
        </Button>
      ) : null}
    </div>
  );
}

function Leaders({ title, note, items, loading, empty }: { title: string; note: ReactNode; items: Parameters<typeof useTodayRows>[0]; loading: boolean; empty: string }) {
  const t = useT();
  const rows = useTodayRows(items);
  return (
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card" aria-label={title}>
      <header className="flex h-11 shrink-0 items-center border-b border-border px-3.5">
        <h2 className="text-section-title">{title}</h2>
        <span className="ml-auto text-xs text-muted-foreground">{note}</span>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-16" />
            <col className="w-16" />
            <col className="w-20" />
            <col className="w-20" />
          </colgroup>
          <thead className="sticky top-0 z-[var(--z-sticky)] bg-surface-2 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">{t("cj.col.campaign")}</th>
              <th className="px-3 py-2 text-right font-semibold">{t("cols.clicks")}</th>
              <th className="px-3 py-2 text-right font-semibold">{t("cols.leads")}</th>
              <th className="px-3 py-2 text-right font-semibold">{t("cols.profit")}</th>
              <th className="px-3 py-2 text-right font-semibold">{t("cols.roi")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-3">
                  <Skeleton className="h-24 w-full" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map((r) => <LeaderRow key={r.id} r={r} />)
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LeaderRow({ r }: { r: TodayRow }) {
  const tone = (n: number | undefined) => ((n ?? 0) > 0 ? "text-ok" : (n ?? 0) < 0 ? "text-destructive" : "");
  return (
    <tr className="border-t border-border hover:bg-surface-2">
      <td className="truncate px-3 py-1.5">
        <Link to={`/campaigns/${r.id}`} className="hover:text-primary">
          {r.name}
        </Link>
      </td>
      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatInt(r.v.clicks)}</td>
      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatInt(r.v.leads)}</td>
      <td className={cn("px-3 py-1.5 text-right font-mono tabular-nums", tone(r.v.profit))}>{formatMoney(r.v.profit, { signed: true })}</td>
      <td className={cn("px-3 py-1.5 text-right font-mono tabular-nums", tone(r.v.roi))}>{formatPct(r.v.roi, { digits: 0, signed: true })}</td>
    </tr>
  );
}

/** «Сегодня» at phone width (доска MToday). Every row leads somewhere; nothing pauses a campaign. */
function TodayPhone({ today, at }: { today: ReturnType<typeof useToday>; at: string }) {
  const t = useT();
  const { state, tz, model, day } = today;
  const attention = useAttention(day.data);
  const v = model?.today;
  const p = model?.yesterdayByNow;
  const loading = state === "loading" || !model || !v || !p;
  const signed = (n: number | undefined) => (n === undefined || n === 0 ? "neutral" : n > 0 ? "ok" : "danger");
  const tiles: MetricTile[] = [
    { key: "clicks", label: t("cols.clicks"), value: formatInt(v?.clicks), delta: cardDelta("clicks", "count", v?.clicks, p?.clicks) },
    { key: "leads", label: t("cols.leads"), value: formatInt(v?.leads), delta: cardDelta("leads", "count", v?.leads, p?.leads) },
    { key: "approved", label: t("cols.approved"), value: formatInt(v?.conversions), note: t("adapt.approve", { pct: formatPct(v?.approve_rate, { digits: 0 }) }) },
    { key: "hold", label: t("kpi.hold"), value: formatInt(v?.conversions_hold), note: v?.revenue_in_transit !== undefined ? `≈ ${formatMoney(v.revenue_in_transit)}` : undefined },
    { key: "cost", label: t("cols.cost"), value: formatMoney(v?.cost), delta: cardDelta("cost", "count", v?.cost, p?.cost) },
    { key: "roi", label: t("cols.roi"), value: formatPct(v?.roi, { digits: 0, signed: true }), delta: cardDelta("roi", "pp", v?.roi, p?.roi), tone: signed(v?.roi) },
  ];
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3.5 pb-4 pt-3.5">
      {state === "error" ? (
        <PageState mode="error" title={t("today.error")} error={today.error} retry={today.retry} retryLabel={t("lib.common.retry")} />
      ) : state === "first" ? (
        <ReadinessChecklist />
      ) : (
        <>
          <div className="flex shrink-0 flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">{t("adapt.today.caption", { at, tz })}</span>
            {loading ? (
              <Skeleton className="h-11 w-48" />
            ) : (
              <div className="flex items-baseline gap-2.5">
                <span className={cn("font-mono text-3xl font-semibold leading-tight tracking-tight tabular-nums", toneText(v.profit))}>{formatMoney(v.profit, { signed: true })}</span>
                {v.profit !== undefined && p.profit !== undefined ? (
                  <span className="text-sm">
                    <span className={cn("font-mono tabular-nums", toneText(v.profit - p.profit))}>{formatMoney(v.profit - p.profit, { signed: true })}</span>{" "}
                    <span className="text-muted-foreground">{t("adapt.today.vsYesterday")}</span>
                  </span>
                ) : null}
              </div>
            )}
          </div>
          <section className="flex shrink-0 flex-col rounded-lg border border-border bg-card px-3.5 pb-2 pt-3" aria-label={t("today.chart.profit")}>
            {loading ? (
              <Skeleton className="h-33 w-full" />
            ) : (
              <div className="flex h-33">
                <RunChart
                  today={model.runToday.map((b) => b.profit ?? 0)}
                  yesterday={model.runYesterday.map((b) => b.profit ?? 0)}
                  fmt={(n) => formatMoney(n, { signed: true })}
                  nowLabel=""
                  wasLabel=""
                  compact
                />
              </div>
            )}
            <div className="flex gap-3.5 pt-1.5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <i aria-hidden className="inline-block size-2.5 rounded-sm bg-primary" />
                {t("today.legend.today")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i aria-hidden className="inline-block size-2.5 rounded-sm bg-muted-foreground" />
                {t("today.legend.yesterday")}
              </span>
            </div>
          </section>
          <MetricGrid tiles={tiles} loading={loading} label={t("nav.today")} />
          <div className="flex shrink-0 items-center justify-between pt-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("today.attention")}</h2>
            <span className="font-mono text-xs text-muted-foreground">{formatInt(attention.length)}</span>
          </div>
          <div className="flex shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
            {loading ? (
              <Skeleton className="m-3.5 h-24" />
            ) : attention.length === 0 ? (
              <p className="px-3.5 py-5 text-center text-sm text-muted-foreground">{t("today.attention.calm")}</p>
            ) : (
              attention.map((r, i) => {
                const body = (
                  <>
                    <StatusDot tone={r.tone === "info" ? "info" : r.tone} className="shrink-0" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">{r.title}</span>
                      <span className="truncate text-xs text-muted-foreground">{r.sub}</span>
                    </span>
                    {r.action ? <ChevronRight className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} /> : null}
                  </>
                );
                const cls = "flex min-h-13 items-center gap-2.5 border-t border-border px-3.5 py-2 first:border-t-0";
                return r.action ? (
                  <Link key={i} to={r.action.to} className={cn(cls, "hover:bg-surface-2")}>
                    {body}
                  </Link>
                ) : (
                  <div key={i} className={cls}>
                    {body}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

function toneText(n: number | undefined): string | undefined {
  return (n ?? 0) > 0 ? "text-ok" : (n ?? 0) < 0 ? "text-destructive" : undefined;
}

function hhmm(c: { hour: number; minute: number }): string {
  return `${String(c.hour).padStart(2, "0")}:${String(c.minute).padStart(2, "0")}`;
}

/** "20 октября" in the product's language (not the machine's). */
function humanDate(iso: string, locale: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US", { day: "numeric", month: "long", timeZone: "UTC" });
}

